import { useEffect, useState } from 'react';
import { listUserTeams, type UserTeam } from '../api/userTeams';

/**
 * Shared cache for `GET /user-teams`. User-teams feed the ViewerToggle on
 * every list view + the Settings Teams tab, so a single module-level cache
 * keeps the request count to one per session (refreshable after mutations).
 *
 * Hydrates from `localStorage['userteams.v1']` on module load so the
 * ViewerToggle renders its Teams section without a blank flash on cold
 * starts. Entries older than 1 hour are ignored — a stale user-team
 * listing is cheap to reload in the background, so we bias toward freshness.
 */

const STORAGE_KEY = 'userteams.v1';
const MAX_STALE_MS = 60 * 60 * 1000;

interface StoredUserTeams {
  userTeams: UserTeam[];
  at: number;
}

function loadFromStorage(): UserTeam[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredUserTeams;
    if (!Array.isArray(parsed?.userTeams) || typeof parsed.at !== 'number') return null;
    if (Date.now() - parsed.at > MAX_STALE_MS) return null;
    return parsed.userTeams;
  } catch {
    return null;
  }
}

function saveToStorage(userTeams: UserTeam[] | null): void {
  try {
    if (!userTeams) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ userTeams, at: Date.now() } satisfies StoredUserTeams)
      );
    }
  } catch {
    // Storage quota or private mode — non-fatal.
  }
}

const listeners = new Set<() => void>();
let cache: UserTeam[] | null = loadFromStorage();
let inflight: Promise<void> | null = null;
let hasInitialized = false;
// Stable ref for the empty case so downstream memos (viewerFiltered, etc.)
// don't re-compute on every render when cache is null.
const EMPTY_USER_TEAMS: UserTeam[] = [];

export async function refreshUserTeams(): Promise<void> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      cache = await listUserTeams();
      saveToStorage(cache);
      for (const l of listeners) l();
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function clearUserTeamsCache(): void {
  cache = null;
  hasInitialized = false;
  saveToStorage(null);
  for (const l of listeners) l();
}

export function useUserTeams(): {
  userTeams: UserTeam[];
  refresh: () => Promise<void>;
} {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = (): void => force((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  useEffect(() => {
    if (hasInitialized) return;
    hasInitialized = true;
    if (!inflight) {
      void refreshUserTeams().catch(() => {
        hasInitialized = false;
      });
    }
  }, []);
  return { userTeams: cache ?? EMPTY_USER_TEAMS, refresh: refreshUserTeams };
}
